import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getPayrocSessionToken } from 'api/Transaction';

import { GENERIC_ERROR } from 'lib/constants/messages';

import { getAchStyles, getCardStyles } from '../lib/styles';
import { achFields, achWrapperMap, cardFields, cardWrapperMap } from '../lib/fields';

const PAYROC_HOSTED_FIELDS_SCRIPT = process.env.REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT;

export const usePayroc = ({
  type,
  containerRef,
  amount,
  handlePayment,
  validate,
  id,
  mode = 'payment',
  buttonText,
  secureTokenId,
  terminalPin,
  onError = () => {}
}) => {
  const [_dataBToken, setDataBToken] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldFormCard, setFieldFormCard] = useState(null);
  const [fieldFormACH, setFieldFormACH] = useState(null);

  const isCard = type === 'card';
  const isAch = type === 'ach';

  const requiredFields = isCard
    ? ['cardholderName', 'cardNumber', 'cvv', 'expiryDate']
    : ['nameOnAccount', 'accountType', 'achAccountNumber', 'routingNumber'];

  const fieldValidityRef = useRef({});
  const handlePaymentRef = useRef(handlePayment);
  const validateRef = useRef(validate);

  useEffect(() => {
    handlePaymentRef.current = handlePayment;
  }, [handlePayment]);

  useEffect(() => {
    validateRef.current = validate;
  }, [validate]);

  const navigate = useNavigate();
  const script = document.createElement('script');

  useEffect(() => {
    try {
      script.src = PAYROC_HOSTED_FIELDS_SCRIPT;
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () =>
        setError({ error: "Couldn't load the payment form. Please try again." });
      document.body.appendChild(script);
    } catch (err) {
      console.error('Error loading 3rd-party script:', err);
      setError({ error: "Couldn't load the payment form. Please try again." });
    }

    return () => {
      document.body.removeChild(script);
    };
  }, [id]);

  const initializeForm = useCallback(
    async (formFields, wrapperMap) => {
      const Payroc = window.Payroc;

      try {
        setIsLoading(true);
        const res = await getPayrocSessionToken(navigate, {
          scenario: mode,
          secureTokenId,
          terminalPin
        });
        const sessionToken = res?.token;

        if (!sessionToken) {
          const message = GENERIC_ERROR;
          setError({ error: message });
          onError(message);
          return;
        }

        setDataBToken(sessionToken);

        if (containerRef.current && Payroc.hostedFields && sessionToken) {
          const instance = new Payroc.hostedFields({
            ...formFields,
            sessionToken,
            onPreSubmit: async () => {
              const allFieldsValid = requiredFields.every(
                (f) => fieldValidityRef.current[f] === true
              );
              if (!allFieldsValid) return false;
              if (validateRef.current) {
                const isValid = await validateRef.current();
                return isValid;
              }
              return true;
            }
          });

          instance.on('error', ({ type, field, message }) => {
            const errorMessage = message || "Couldn't load the payment form. Please try again.";
            setError({ error: errorMessage, type, field });
            onError(errorMessage);
          });

          await instance.initialize();

          instance.on('submissionSuccess', async (data) => {
            if (!handlePaymentRef.current) {
              setError({
                error: "This payment method isn't supported",
                type: 'error',
                field: 'payment_method'
              });
              return;
            }

            const result = await handlePaymentRef.current(data);
            if (result?.code !== 0) {
              setError(result);
              return;
            }
          });

          instance.on('fieldValidityChange', ({ field, validationError }) => {
            fieldValidityRef.current[field] = !validationError;
            const element = document.querySelector(wrapperMap[field]);
            if (element) {
              if (validationError) {
                element.classList.add('has-error');
                return;
              }
              element.classList.remove('has-error');
            }
          });

          return instance;
        }
      } catch (err) {
        const defaultMsg = GENERIC_ERROR;
        const message = err.error || err.message || defaultMsg;
        setError({ error: message });
        onError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [containerRef]
  );

  const initCardForm = useCallback(async () => {
    if (fieldFormCard || !scriptLoaded) return;
    const formFields = {
      mode,
      fields: { card: cardFields({ amount, buttonText }) },
      styles: getCardStyles()
    };

    const hostedFieldsFormCard = await initializeForm(formFields, cardWrapperMap);

    setFieldFormCard(hostedFieldsFormCard);
  }, [initializeForm, amount, scriptLoaded, buttonText]);

  const initACHForm = useCallback(async () => {
    if (fieldFormACH || !scriptLoaded) return;
    const formFields = {
      mode,
      fields: { ach: achFields({ amount, buttonText }) },
      styles: getAchStyles()
    };

    const hostedFieldsFormACH = await initializeForm(formFields, achWrapperMap);

    setFieldFormACH(hostedFieldsFormACH);
  }, [initializeForm, amount, scriptLoaded, buttonText]);

  const startInitialization = useCallback(() => {
    if (isAch) {
      initACHForm();
    } else if (isCard) {
      initCardForm();
    }
  }, [initACHForm, initCardForm, isCard, isAch]);

  useEffect(() => {
    startInitialization();
  }, [startInitialization]);

  const retry = useCallback(() => {
    setError(null);
    startInitialization();
  }, [startInitialization]);

  return {
    isLoading,
    error,
    retry
  };
};
